import { Box, LoadingOverlay } from '@mantine/core'
import { createFileRoute, Outlet } from '@tanstack/react-router'
import { useHead } from '@unhead/react'
import { useEffect } from 'react'
import { useClientConfig } from '@/entities/client-config/lib'
import { stateConfigOptions, useStateConfigPoller } from '@/entities/state/model'
import { fetchStoreDetails, storeKeys } from '@/entities/store/api'
import { useOrderSounds } from '@/features/alerts/lib'
import { requireAuth } from '@/features/auth/lib'
import { useAuthStore } from '@/features/auth/model'
import { DriverJsProvider } from '@/features/onboarding/ui'
import { CollectionCodeProvider } from '@/features/order/ui/collection-code-modal'
import { InitialsCaptureProvider } from '@/features/order/ui/initials-capture-modal'
import { NoBarcodeConfirmProvider } from '@/features/order/ui/no-barcode-confirmation'
import { OrderDetailsModalProvider } from '@/features/order/ui/order-details/modal'
import { RealtimeProvider } from '@/features/realtime/lib'
import { useSettingsStore } from '@/shared/model/settings.store'
import { DashboardShell } from '@/widgets/dashboard-layout'

export const Route = createFileRoute('/_dashboard')({
  component: RouteComponent,
  beforeLoad: () => requireAuth(),
  loader: async ({ context: { queryClient } }) => {
    // Check if user is authenticated
    const authState = useAuthStore.getState()
    if (!authState.isAuthenticated()) {
      return
    }

    await Promise.all([
      queryClient.prefetchQuery(stateConfigOptions()),
      queryClient.prefetchQuery({
        queryKey: storeKeys.details(),
        queryFn: fetchStoreDetails,
      }),
    ])
  },
})

function RouteComponent() {
  const isLoading = useAuthStore(state => state.isLoading)
  const { config, isLoaded } = useClientConfig()
  const setAllowSound = useSettingsStore(state => state.setAllowSound)

  useStateConfigPoller()
  useOrderSounds()

  useHead({
    style: [{
      innerHTML: `
        :root {
          --hs-theme-background-color-gradient-one: ${config.theme.backgroundColorOne};
          --hs-theme-background-color-gradient-two: ${config.theme.backgroundColorTwo};
        }
      `,
    }],
  })

  // Initialize sound protection - allow sound after 2 seconds of dashboard mount
  useEffect(() => {
    const timer = setTimeout(() => {
      setAllowSound(true)
    }, 2000)

    return () => clearTimeout(timer)
  }, [setAllowSound])

  return (
    <CollectionCodeProvider>
      <InitialsCaptureProvider>
        <NoBarcodeConfirmProvider>
          <DriverJsProvider>
            <RealtimeProvider>
              <Box style={{ position: 'relative', minHeight: '100vh' }}>
                <LoadingOverlay
                  visible={isLoading || !isLoaded}
                  zIndex={1000}
                  overlayProps={{
                    backgroundOpacity: isLoaded ? 0.75 : 1,
                    radius: 'sm',
                    blur: 2,
                  }}
                />
                <DashboardShell>
                  <Outlet />
                  <OrderDetailsModalProvider />
                </DashboardShell>
              </Box>
            </RealtimeProvider>
          </DriverJsProvider>
        </NoBarcodeConfirmProvider>
      </InitialsCaptureProvider>
    </CollectionCodeProvider>
  )
}
